import { oneOfType, number, string, objectOf, arrayOf } from "prop-types"

const PX_SCALE = [0, 2, 4, 8, 16, 32, 64, 128, 256, 512]
const PC_SCALE = Object.assign([], PX_SCALE, { 1: "100%" })
const BREAKPOINTS = ["all", "sm", "md", "lg", "xl"].map((alias, index) => ({
  alias,
  value: index * 360
}))

const IGNORE = /\s|\d+\.\d+/
const NUMBER = /^-?(\d*\.)?\d+$/
const push = Array.prototype.push
const isArray = Array.isArray
const isNil = (x) => x == null
const isUndefined = (x) => x === undefined
const isType = (type) => (x) => typeof x === type
const isFunction = isType("function")
const isObject = isType("object")
const isPlainObject = (x) => !!x && isObject(x) && !isArray(x)
const isString = isType("string")
const isTypeNumber = isType("number")
const isNumber = (x) => isTypeNumber(x) && !isNaN(x)
const isNumberLike = (x) => isNumber(x) || (isString(x) && NUMBER.test(x))
const isUnitless = (x) => isNumberLike(x) && !!+x
const isFraction = (x) => isUnitless(x) && x > -1 && x < 1
const when = (p) => (f) => (x) => (p(x) ? f(x) : x)
const addPc = when(isFraction)((x) => x * 100 + "%")
const addPx = when(isUnitless)((x) => x + "px")
const addRem = when(isUnitless)((x) => x + "rem")
const addPcOrPx = (x) => addPx(addPc(x))
const mq = (x) => `@media(min-width: ${addPx(x)})`
const toArray = (args) => (isArray(args[0]) ? args[0] : args)
const toPath = (x) => (isString(x) && !IGNORE.test(x) ? x.split(".") : [x])
function get(path, lookup) {
  if (isNil(path) || isNil(lookup)) return undefined
  if ((isArray(path) ? path[0] : path) === ".") return lookup
  const keys = isArray(path) ? path.concat() : toPath(path)
  const head = keys[0]
  const isKey = isString(head)
  const invert = (isKey && head.indexOf("-") === 0) || head < 0
  if (invert) keys[0] = isKey ? head.substring(1) : Math.abs(head)
  const value = keys.reduce((v, k) => {
    let r = v && v[k]
    if (r && r.alias) r = r.value
    else if (isArray(v)) {
      const a = v.find((o) => o && o.alias === k)
      if (a) r = a.value
    }
    return r
  }, lookup)
  return isNumber(value)
    ? (invert ? -1 : 1) * value
    : isString(value)
    ? (invert ? "-" : "") + value
    : value
}
function resolve(paths, lookup) {
  if (isNil(lookup) || !isArray(paths)) return undefined
  return paths.reduceRight((v, p) => {
    const r = get(p, lookup)
    return isUndefined(r) ? v : r
  }, undefined)
}
function merge(array, initial = {}) {
  return array.reduce((acc, val) => {
    return isArray(val) ? merge(val, initial) : Object.assign(acc, val)
  }, initial)
}

const KEYS = ["propsKeys", "styleKeys", "themeKeys"]
function unique(renderers, initial = []) {
  return renderers.reduce((collection, renderer) => {
    const isComposed = renderer.type === "compose"
    const isIncluded = collection.includes(renderer)
    const { renderers: renderFuncs } = renderer.options
    if (!isComposed && !isIncluded) collection.push(renderer)
    if (isArray(renderFuncs)) unique(renderFuncs, initial)
    return collection
  }, initial)
}
function compose(options) {
  let { name } = options
  if (!/Set$/.test(name)) name += "Set"
  const renderers = unique(options.renderers)
  const composedOptions = {
    propsKeys: [],
    styleKeys: [],
    themeKeys: [],
    renderers
  }
  renderers.forEach((renderer) =>
    KEYS.forEach((optionsKey) => {
      const composedKeys = composedOptions[optionsKey]
      const rendererKeys = renderer.options[optionsKey]
      if (rendererKeys) {
        rendererKeys.forEach((key) => {
          if (!composedKeys.includes(key)) composedKeys.push(key)
        })
      }
    })
  )
  const renderProps = (props) => {
    const result = renderers.reduce((styles, renderer) => {
      const output = renderer(props)
      if (output) push.apply(styles, output)
      return styles
    }, [])
    return result.length ? result : null
  }
  Object.defineProperty(renderProps, "name", { value: name })
  renderProps.type = "compose"
  renderProps.options = composedOptions
  return renderProps
}

function interpolate(options) {
  let { name, renderers } = options
  if (!/Transform$/.test(name)) name += "Transform"
  const renderer = compose({ name, renderers })
  const { propsKeys, styleKeys } = renderer.options
  const transform = (style, theme) => {
    if (!isPlainObject(style)) return null
    const renderedStyle = renderer(Object.assign({ theme }, style))
    const mergedStyle = renderedStyle && merge(renderedStyle)
    return Object.keys(style).reduce((result, key) => {
      const value = result[key]
      if (isPlainObject(value)) {
        result[key] = transform(value, theme)
      } else {
        const hasPropsKey = propsKeys.includes(key)
        const hasStyleKey = styleKeys.includes(key)
        if (hasPropsKey && !hasStyleKey) delete result[key]
      }
      return result
    }, Object.assign({}, style, mergedStyle))
  }
  Object.defineProperty(transform, "name", { value: name })
  transform.renderer = renderer
  transform.options = options
  return transform
}

function filter(options) {
  const { propsKeys, renderers, reducer, initial } = options
  const keys = []
  if (isArray(propsKeys)) {
    push.apply(keys, propsKeys)
  }
  if (isArray(renderers)) {
    const filterSet = compose({ name: "filter", renderers })
    push.apply(keys, filterSet.options.propsKeys)
  }
  const filterProps = (props) => {
    return keys.reduce((acc, key) => {
      return reducer(acc, key, props)
    }, initial(props))
  }
  return filterProps
}
function omit(options) {
  return filter({
    propsKeys: options.propsKeys,
    renderers: options.renderers,
    initial: (props) => Object.assign({}, props),
    reducer: (acc, key) => {
      delete acc[key]
      return acc
    }
  })
}
function pick(options) {
  return filter({
    propsKeys: options.propsKeys,
    renderers: options.renderers,
    initial: () => ({}),
    reducer: (acc, key, props) => {
      const value = props[key]
      if (!isNil(value)) acc[key] = value
      return acc
    }
  })
}

function render(keys, value) {
  if (isNil(value) || !isArray(keys) || !keys.length) return null
  return keys.reduce((s, k) => {
    s[k] = value
    return s
  }, {})
}

function style(options) {
  const {
    propsKeys,
    styleKeys,
    themeKeys,
    transform,
    renderers,
    defaults
  } = options
  if (!isArray(propsKeys) || propsKeys.length < 1) {
    throw Error("propsKeys must be an array containing at least one key")
  }
  const name = propsKeys[0]
  const keys = isArray(styleKeys) ? styleKeys : propsKeys.slice(0, 1)
  const transformer =
    isArray(renderers) &&
    renderers.length > 0 &&
    interpolate({ name, renderers })
  if (isUndefined(styleKeys)) options.styleKeys = keys
  const renderValue = (value, theme) => {
    let themeValue = resolve(themeKeys, theme)
    if (!isUndefined(themeValue)) {
      if (value !== ".") {
        themeValue = resolve(themeKeys.map((k) => `${k}.${value}`), theme)
      }
      if (!isNil(themeValue)) value = themeValue
    } else if (defaults) {
      const defaultValue = get(value, defaults)
      if (!isNil(defaultValue)) value = defaultValue
    }
    if (isFunction(transform)) value = transform(value)
    if (styleKeys === null) {
      return isPlainObject(value) ? value : null
    } else {
      return render(keys, value)
    }
  }
  const renderProps = (props) => {
    const propsValue = resolve(propsKeys, props)
    if (isNil(propsValue)) return null
    const { theme } = props
    const styles = []
    const pushStyle = (value, query) => {
      let result = renderValue(value, theme)
      if (result) {
        if (transformer) result = transformer(result, theme)
        if (query) result = { [query]: result }
        styles.push(result)
      }
    }
    if (isObject(propsValue)) {
      const themeBreaks = theme && theme.breakpoints
      const breakpoints = isUndefined(themeBreaks) ? BREAKPOINTS : themeBreaks
      if (isArray(breakpoints)) {
        breakpoints.forEach((value, index) => {
          const breakpoint = get(index, breakpoints)
          const isZero = parseInt(breakpoint, 10) === 0
          const query = isZero ? null : mq(breakpoint)
          const styleValue = resolve([index, value.alias], propsValue)
          if (!isNil(styleValue)) pushStyle(styleValue, query)
        })
      }
    } else pushStyle(propsValue)
    return styles.length ? styles : null
  }
  Object.defineProperty(renderProps, "name", { value: name })
  renderProps.type = "style"
  renderProps.options = options
  if (transformer) renderProps.transformer = transformer
  return renderProps
}
function variant(options) {
  const renderProps = style(Object.assign({}, options, { styleKeys: null }))
  renderProps.type = "variant"
  return renderProps
}
function extend(a) {
  return (b) => style(Object.assign({}, a, b))
}

const alignContent = style({
  propsKeys: ["alignContent", "alc"]
})
const alignItems = style({
  propsKeys: ["alignItems", "ali"]
})
const alignSelf = style({
  propsKeys: ["alignSelf", "als"]
})
const justifyContent = style({
  propsKeys: ["justifyContent", "jfc"]
})
const justifyItems = style({
  propsKeys: ["justifyItems", "jfi"]
})
const justifySelf = style({
  propsKeys: ["justifySelf", "jfs"]
})
const placeContent = style({
  propsKeys: ["placeContent", "plc"]
})
const placeItems = style({
  propsKeys: ["placeItems", "pli"]
})
const placeSelf = style({
  propsKeys: ["placeSelf", "pls"]
})
const order = style({
  propsKeys: ["order", "ord"]
})

const animation = style({
  propsKeys: ["animation"],
  themeKeys: ["animations"]
})
const transition = style({
  propsKeys: ["transition"],
  themeKeys: ["transitions"]
})

const ex = extend({
  themeKeys: ["colors"],
  defaults: {
    gray: ["#EEE", "#AAA", "#666"],
    text: "#222",
    link: "#00F"
  }
})
const background = ex({
  propsKeys: ["background", "bg"]
})
const backgroundColor = ex({
  propsKeys: ["backgroundColor", "bgc"]
})
const borderColor = ex({
  propsKeys: ["borderColor", "bdc"]
})
const color = ex({
  propsKeys: ["color", "tc"]
})
const fill = ex({
  propsKeys: ["fill", "fc"]
})
const outlineColor = ex({
  propsKeys: ["outlineColor", "olc"]
})
const colorSet = compose({
  name: "color",
  renderers: [
    background,
    backgroundColor,
    borderColor,
    color,
    fill,
    outlineColor
  ]
})

const ex$1 = extend({
  transform: addPcOrPx,
  defaults: [0, "100%"]
})
const backgroundImage = style({
  propsKeys: ["backgroundImage", "bgi"]
})
const backgroundPosition = ex$1({
  propsKeys: ["backgroundPosition", "bgp"]
})
const backgroundRepeat = style({
  propsKeys: ["backgroundRepeat", "bgr"]
})
const backgroundSize = ex$1({
  propsKeys: ["backgroundSize", "bgs"]
})
const backgroundSet = compose({
  name: "background",
  renderers: [
    background,
    backgroundColor,
    backgroundImage,
    backgroundPosition,
    backgroundRepeat,
    backgroundSize
  ]
})

const ex$2 = extend({
  themeKeys: ["borders"],
  transform: addPx
})
const border = ex$2({
  propsKeys: ["border", "bd"]
})
const borderTop = ex$2({
  propsKeys: ["borderTop", "bdt"]
})
const borderRight = ex$2({
  propsKeys: ["borderRight", "bdr"]
})
const borderBottom = ex$2({
  propsKeys: ["borderBottom", "bdb"]
})
const borderLeft = ex$2({
  propsKeys: ["borderLeft", "bdl"]
})
const borderStyle = style({
  propsKeys: ["borderStyle", "bds"],
  themeKeys: ["borderStyles"]
})
const borderWidth = ex$2({
  propsKeys: ["borderWidth", "bdw"],
  themeKeys: ["borderWidths"]
})
const borderRadius = ex$2({
  propsKeys: ["borderRadius", "radius", "rad"],
  themeKeys: ["borderRadii", "sizes"],
  transform: addPcOrPx,
  defaults: PX_SCALE
})
const borderSet = compose({
  name: "border",
  renderers: [
    border,
    borderTop,
    borderRight,
    borderBottom,
    borderLeft,
    borderColor,
    borderStyle,
    borderWidth,
    borderRadius
  ]
})

const display = style({
  propsKeys: ["display", "d"]
})
const opacity = style({
  propsKeys: ["opacity", "o"],
  themeKeys: ["opacities"]
})
const overflow = style({
  propsKeys: ["overflow", "of"]
})
const overflowX = style({
  propsKeys: ["overflowX", "ofx"]
})
const overflowY = style({
  propsKeys: ["overflowY", "ofy"]
})
const visibility = style({
  propsKeys: ["visibility", "vis"]
})
const overflowSet = compose({
  name: "overflow",
  renderers: [overflow, overflowX, overflowY]
})
const displaySet = compose({
  name: "display",
  renderers: [display, opacity, overflowSet, visibility]
})

const flex = style({
  propsKeys: ["flex", "fx"]
})
const flexBasis = style({
  propsKeys: ["flexBasis", "fxb"]
})
const flexGrow = style({
  propsKeys: ["flexGrow", "fxg"]
})
const flexShrink = style({
  propsKeys: ["flexShrink", "fxs"]
})
const flexFlow = style({
  propsKeys: ["flexFlow", "fxf"]
})
const flexDirection = style({
  propsKeys: ["flexDirection", "fxd"]
})
const flexWrap = style({
  propsKeys: ["flexWrap", "fxw"]
})
const flexParentSet = compose({
  name: "flexParent",
  renderers: [
    display,
    alignItems,
    alignContent,
    justifyItems,
    justifyContent,
    flexFlow,
    flexDirection,
    flexWrap
  ]
})
const flexChildSet = compose({
  name: "flexChild",
  renderers: [
    alignSelf,
    justifySelf,
    order,
    flex,
    flexBasis,
    flexGrow,
    flexShrink
  ]
})
const flexSet = compose({
  name: "flex",
  renderers: [flexParentSet, flexChildSet]
})

const ex$3 = extend({
  themeKeys: ["spaces"],
  transform: addPx,
  defaults: PX_SCALE
})
const grid = style({
  propsKeys: ["grid", "g"]
})
const gridTemplate = style({
  propsKeys: ["gridTemplate", "gt"]
})
const gridTemplateRows = style({
  propsKeys: ["gridTemplateRows", "gtr"]
})
const gridTemplateColumns = style({
  propsKeys: ["gridTemplateColumns", "gtc"]
})
const gridTemplateAreas = style({
  propsKeys: ["gridTemplateAreas", "gta"]
})
const gridGap = ex$3({
  propsKeys: ["gridGap", "gg"]
})
const gridRowGap = ex$3({
  propsKeys: ["gridRowGap", "grg"]
})
const gridColumnGap = ex$3({
  propsKeys: ["gridColumnGap", "gcg"]
})
const gridAutoRows = style({
  propsKeys: ["gridAutoRows", "gar"]
})
const gridAutoColumns = style({
  propsKeys: ["gridAutoColumns", "gac"]
})
const gridAutoFlow = style({
  propsKeys: ["gridAutoFlow", "gaf"]
})
const gridArea = style({
  propsKeys: ["gridArea", "ga"]
})
const gridRow = style({
  propsKeys: ["gridRow", "gr"]
})
const gridRowStart = style({
  propsKeys: ["gridRowStart", "grs"]
})
const gridRowEnd = style({
  propsKeys: ["gridRowEnd", "gre"]
})
const gridColumn = style({
  propsKeys: ["gridColumn", "gc"]
})
const gridColumnStart = style({
  propsKeys: ["gridColumnStart", "gcs"]
})
const gridColumnEnd = style({
  propsKeys: ["gridColumnEnd", "gce"]
})
const gridParentSet = compose({
  name: "gridParent",
  renderers: [
    display,
    placeItems,
    placeContent,
    alignItems,
    alignContent,
    justifyItems,
    justifyContent,
    grid,
    gridTemplate,
    gridTemplateRows,
    gridTemplateColumns,
    gridTemplateAreas,
    gridGap,
    gridRowGap,
    gridColumnGap,
    gridAutoRows,
    gridAutoColumns,
    gridAutoFlow
  ]
})
const gridChildSet = compose({
  name: "gridChild",
  renderers: [
    placeSelf,
    alignSelf,
    justifySelf,
    gridArea,
    gridRow,
    gridRowStart,
    gridRowEnd,
    gridColumn,
    gridColumnStart,
    gridColumnEnd
  ]
})
const gridSet = compose({
  name: "grid",
  renderers: [gridParentSet, gridChildSet]
})

const SK = "sizes"
const ex$4 = extend({
  themeKeys: [SK],
  transform: addPcOrPx,
  defaults: PC_SCALE
})
const verticalAlign = style({
  propsKeys: ["verticalAlign", "va"]
})
const position = style({
  propsKeys: ["position", "pos"]
})
const zIndex = style({
  propsKeys: ["zIndex", "zi"],
  themeKeys: ["zIndices"]
})
const top = ex$4({
  propsKeys: ["top", "t"]
})
const right = ex$4({
  propsKeys: ["right", "r"]
})
const bottom = ex$4({
  propsKeys: ["bottom", "b"]
})
const left = ex$4({
  propsKeys: ["left", "l"]
})
const width = ex$4({
  propsKeys: ["width", "w"],
  themeKeys: ["widths", SK]
})
const minWidth = ex$4({
  propsKeys: ["minWidth", "minw"],
  themeKeys: ["minWidths", SK]
})
const maxWidth = ex$4({
  propsKeys: ["maxWidth", "maxw"],
  themeKeys: ["maxWidths", SK]
})
const height = ex$4({
  propsKeys: ["height", "h"],
  themeKeys: ["heights", SK]
})
const minHeight = ex$4({
  propsKeys: ["minHeight", "minh"],
  themeKeys: ["minHeights", SK]
})
const maxHeight = ex$4({
  propsKeys: ["maxHeight", "maxh"],
  themeKeys: ["maxHeights", SK]
})
const size = ex$4({
  propsKeys: ["size", "s"],
  styleKeys: ["width", "height"]
})
const positionSet = compose({
  name: "position",
  renderers: [position, zIndex, top, right, bottom, left]
})
const sizeSet = compose({
  name: "size",
  renderers: [size, width, minWidth, maxWidth, height, minHeight, maxHeight]
})
const layoutSet = compose({
  name: "layout",
  renderers: [display, verticalAlign, positionSet, sizeSet]
})

const ex$5 = extend({
  transform: addPx
})
const outline = ex$5({
  propsKeys: ["outline", "ol"],
  themeKeys: ["outlines", "borders"]
})
const outlineOffset = ex$5({
  propsKeys: ["outlineOffset", "olo"],
  themeKeys: ["outlineOffsets"]
})
const outlineStyle = style({
  propsKeys: ["outlineStyle", "ols"],
  themeKeys: ["outlineStyles", "borderStyles"]
})
const outlineWidth = ex$5({
  propsKeys: ["outlineWidth", "olw"],
  themeKeys: ["outlineWidths", "borderWidths"]
})
const outlineSet = compose({
  name: "outline",
  renderers: [outline, outlineColor, outlineOffset, outlineStyle, outlineWidth]
})

const boxShadow = style({
  propsKeys: ["boxShadow", "bsh"],
  themeKeys: ["boxShadows"]
})
const textShadow = style({
  propsKeys: ["textShadow", "tsh"],
  themeKeys: ["textShadows"]
})

const MT = "marginTop"
const MR = "marginRight"
const MB = "marginBottom"
const ML = "marginLeft"
const PT = "paddingTop"
const PR = "paddingRight"
const PB = "paddingBottom"
const PL = "paddingLeft"
const ex$6 = extend({
  themeKeys: ["spaces"],
  transform: addPx,
  defaults: PX_SCALE
})
const marginTop = ex$6({
  propsKeys: [MT, "mt"]
})
const marginRight = ex$6({
  propsKeys: [MR, "mr"]
})
const marginBottom = ex$6({
  propsKeys: [MB, "mb"]
})
const marginLeft = ex$6({
  propsKeys: [ML, "ml"]
})
const marginX = ex$6({
  propsKeys: ["marginX", "mx"],
  styleKeys: [ML, MR]
})
const marginY = ex$6({
  propsKeys: ["marginY", "my"],
  styleKeys: [MT, MB]
})
const margin = ex$6({
  propsKeys: ["margin", "m"]
})
const paddingTop = ex$6({
  propsKeys: [PT, "pt"]
})
const paddingRight = ex$6({
  propsKeys: [PR, "pr"]
})
const paddingBottom = ex$6({
  propsKeys: [PB, "pb"]
})
const paddingLeft = ex$6({
  propsKeys: [PL, "pl"]
})
const paddingX = ex$6({
  propsKeys: ["paddingX", "px"],
  styleKeys: [PL, PR]
})
const paddingY = ex$6({
  propsKeys: ["paddingY", "py"],
  styleKeys: [PT, PB]
})
const padding = ex$6({
  propsKeys: ["padding", "p"]
})
const marginSet = compose({
  name: "margin",
  renderers: [
    margin,
    marginX,
    marginY,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft
  ]
})
const paddingSet = compose({
  name: "padding",
  renderers: [
    padding,
    paddingX,
    paddingY,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft
  ]
})
const spaceSet = compose({
  name: "space",
  renderers: [marginSet, paddingSet]
})

const fontFamily = style({
  propsKeys: ["fontFamily", "ff"],
  themeKeys: ["fontFamilies"],
  defaults: {
    main: "system-ui, sans-serif",
    mono: "Monaco, monospace"
  }
})
const fontSize = style({
  propsKeys: ["fontSize", "fs"],
  themeKeys: ["fontSizes"],
  transform: addPx,
  defaults: [12, 14, 16, 20, 24, 32, 40, 48, 56, 64]
})
const fontSmoothing = style({
  propsKeys: ["fontSmoothing", "fsm"],
  styleKeys: ["WebkitFontSmoothing"]
})
const fontStyle = style({
  propsKeys: ["fontStyle", "fst"]
})
const fontWeight = style({
  propsKeys: ["fontWeight", "fw"],
  themeKeys: ["fontWeights"],
  defaults: {
    normal: 400,
    bold: 700
  }
})
const lineHeight = style({
  propsKeys: ["lineHeight", "lh"],
  themeKeys: ["lineHeights"],
  defaults: {
    normal: 1.5,
    narrow: 1.25,
    single: 1
  }
})
const letterSpacing = style({
  propsKeys: ["letterSpacing", "ls"],
  themeKeys: ["letterSpacings"],
  transform: addPx
})
const textAlign = style({
  propsKeys: ["textAlign", "ta"]
})
const textDecoration = style({
  propsKeys: ["textDecoration", "td"]
})
const textTransform = style({
  propsKeys: ["textTransform", "tt"]
})
const textSet = compose({
  name: "text",
  renderers: [
    fontFamily,
    fontSize,
    fontSmoothing,
    fontStyle,
    fontWeight,
    lineHeight,
    letterSpacing,
    textAlign,
    textDecoration,
    textTransform,
    color
  ]
})

const perspective = style({
  propsKeys: ["perspective", "ps"]
})
const perspectiveOrigin = style({
  propsKeys: ["perspectiveOrigin", "pso"]
})
const transform = style({
  propsKeys: ["transform", "tf"]
})
const transformOrigin = style({
  propsKeys: ["transformOrigin", "tfo"]
})
const transformSet = compose({
  name: "transform",
  renderers: [perspective, perspectiveOrigin, transform, transformOrigin]
})

const globalSet = compose({
  name: "global",
  renderers: [
    border,
    borderRadius,
    boxShadow,
    opacity,
    outline,
    outlineOffset,
    spaceSet,
    sizeSet,
    textSet,
    colorSet,
    transition
  ]
})

const colorStyle = variant({
  propsKeys: ["colorStyle", "cst"],
  themeKeys: ["colorStyles"],
  renderers: [colorSet]
})
const textStyle = variant({
  propsKeys: ["textStyle", "tst"],
  themeKeys: ["textStyles"],
  renderers: [textSet]
})
const buttonStyle = variant({
  propsKeys: ["buttonStyle", "bst"],
  themeKeys: ["buttonStyles"],
  renderers: [colorStyle, textStyle, globalSet]
})
const gst = variant({
  propsKeys: ["globalStyle", "gst"],
  themeKeys: ["globalStyles"],
  renderers: [buttonStyle, colorStyle, textStyle, globalSet]
})
const globalStyle = (props) => gst(Object.assign({ gst: "." }, props))
globalStyle.transformer = gst.transformer
globalStyle.options = gst.options
globalStyle.type = gst.type

const VALUE_TYPE = oneOfType([number, string])
const OBJECT_TYPE = objectOf(VALUE_TYPE)
const ARRAY_TYPE = arrayOf(VALUE_TYPE)
const PROP_TYPES = oneOfType([VALUE_TYPE, OBJECT_TYPE, ARRAY_TYPE])
function propTypes(...args) {
  return toArray(args).reduce((result, renderer) => {
    renderer.options.propsKeys.forEach((key) => {
      result[key] = PROP_TYPES
    })
    return result
  }, {})
}

export {
  ARRAY_TYPE,
  BREAKPOINTS,
  OBJECT_TYPE,
  PC_SCALE,
  PROP_TYPES,
  PX_SCALE,
  VALUE_TYPE,
  addPc,
  addPcOrPx,
  addPx,
  addRem,
  alignContent,
  alignItems,
  alignSelf,
  animation,
  background,
  backgroundColor,
  backgroundImage,
  backgroundPosition,
  backgroundRepeat,
  backgroundSet,
  backgroundSize,
  border,
  borderBottom,
  borderColor,
  borderLeft,
  borderRadius,
  borderRight,
  borderSet,
  borderStyle,
  borderTop,
  borderWidth,
  bottom,
  boxShadow,
  buttonStyle,
  color,
  colorSet,
  colorStyle,
  compose,
  display,
  displaySet,
  extend,
  fill,
  filter,
  flex,
  flexBasis,
  flexChildSet,
  flexDirection,
  flexFlow,
  flexGrow,
  flexParentSet,
  flexSet,
  flexShrink,
  flexWrap,
  fontFamily,
  fontSize,
  fontSmoothing,
  fontStyle,
  fontWeight,
  get,
  globalSet,
  globalStyle,
  grid,
  gridArea,
  gridAutoColumns,
  gridAutoFlow,
  gridAutoRows,
  gridChildSet,
  gridColumn,
  gridColumnEnd,
  gridColumnGap,
  gridColumnStart,
  gridGap,
  gridParentSet,
  gridRow,
  gridRowEnd,
  gridRowGap,
  gridRowStart,
  gridSet,
  gridTemplate,
  gridTemplateAreas,
  gridTemplateColumns,
  gridTemplateRows,
  height,
  interpolate,
  isArray,
  isFraction,
  isFunction,
  isNil,
  isNumber,
  isNumberLike,
  isObject,
  isPlainObject,
  isString,
  isType,
  isUndefined,
  isUnitless,
  justifyContent,
  justifyItems,
  justifySelf,
  layoutSet,
  left,
  letterSpacing,
  lineHeight,
  margin,
  marginBottom,
  marginLeft,
  marginRight,
  marginSet,
  marginTop,
  marginX,
  marginY,
  maxHeight,
  maxWidth,
  merge,
  minHeight,
  minWidth,
  mq,
  omit,
  opacity,
  order,
  outline,
  outlineColor,
  outlineOffset,
  outlineSet,
  outlineStyle,
  outlineWidth,
  overflow,
  overflowSet,
  overflowX,
  overflowY,
  padding,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingSet,
  paddingTop,
  paddingX,
  paddingY,
  perspective,
  perspectiveOrigin,
  pick,
  placeContent,
  placeItems,
  placeSelf,
  position,
  positionSet,
  propTypes,
  push,
  render,
  resolve,
  right,
  size,
  sizeSet,
  spaceSet,
  style,
  textAlign,
  textDecoration,
  textSet,
  textShadow,
  textStyle,
  textTransform,
  toArray,
  toPath,
  top,
  transform,
  transformOrigin,
  transformSet,
  transition,
  unique,
  variant,
  verticalAlign,
  visibility,
  when,
  width,
  zIndex
}
